<template>
  <div class="client-user-individual-modal">
    <cs-form-dialog
      id="dlg-client-user-individual-modal"
      v-model="clientUserIndividualModalDisplayed"
      body-id="dlg-body-client-user-individual-modal"
      heading="Client User Individual Modal"
      :primary-action="{
        label: 'Save',
        eventName: 'primary-click',
        loading: clientUserSaveActionInProgress,
        disabled: v$.$invalid
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click',
        disabled: clientUserSaveActionInProgress
      }"
      @primary-click="save"
      @secondary1-click="close"
    >
      <template #cs-form-dialog-content>
        <v-container grid-list-md class="form-container">
          <v-row no-gutters class="user-individual-modal-row-with-error">
            <v-col cols="12"> Email </v-col>
            <v-col cols="12">
              <v-text-field
                id="client-user-individual-modal--email"
                v-model="email"
                :disabled="isHubManagedUser"
                variant="solo"
                required
                :error-messages="emailErrors"
                density="compact"
                class="excursion-input"
                placeholder="Enter in the email of the user"
                @keydown.space.prevent
                @update:model-value="v$.email.$touch()"
                @blur="v$.email.$touch()"
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="user-individual-modal-row">
            <v-col cols="12"> Given Name </v-col>
            <v-col cols="12">
              <v-text-field
                id="client-user-individual-modal--given-name"
                v-model="givenName"
                :disabled="isHubManagedUser"
                variant="solo"
                density="compact"
                class="excursion-input"
                placeholder="Enter in the given name of the user"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="user-individual-modal-row">
            <v-col cols="12"> Surname </v-col>
            <v-col cols="12">
              <v-text-field
                id="client-user-individual-modal--surname"
                v-model="surname"
                :disabled="isHubManagedUser"
                variant="solo"
                density="compact"
                class="excursion-input"
                placeholder="Enter in the surname of the user"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="user-individual-modal-row">
            <v-col cols="12"> Client ACL Role </v-col>
            <v-col cols="12">
              <v-select
                id="client-user-individual-modal--client-acl-role"
                v-model="clientAclRole"
                class="type-dropdown excursion-selection"
                placeholder="Select Client ACL Role"
                :items="clientAclRoles"
                item-title="text"
                item-value="value"
                required
                density="compact"
                :error-messages="clientAclRoleErrors"
                variant="solo"
                color="primary"
                hide-details
                @update:model-value="v$.clientAclRole.$touch()"
                @blur="v$.clientAclRole.$touch()"
              >
                <template #item="data">
                  <v-list-item
                    v-bind="data.props"
                    :id="`client-user-individual-modal--client-acl-role-item-${data.item.text}`"
                  ></v-list-item>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <div v-if="inferiorRoleSetupDisplayed" class="user-individual-modal-row">
            <div v-if="clientAdminAssistantRoleDisplaySetupEnabled" class="d-flex align-center">
              <div>
                <v-checkbox-btn
                  id="user-client-admin-assistant-role-display-enabled-tickbox"
                  v-model="clientAdminAssistantRoleDisplayEnabled"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </div>
              <div
                class="client-individual-checkbox-label"
                for="user-client-admin-assistant-role-display-enabled-tickbox"
              >
                Display as <b>client-admin-assistant</b>
              </div>
            </div>
            <div v-if="clientApproverRoleDisplaySetupEnabled" class="d-flex align-center">
              <div>
                <v-checkbox-btn
                  id="user-client-approver-role-display-enabled-tickbox"
                  v-model="clientApproverRoleDisplayEnabled"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </div>
              <div
                class="client-individual-checkbox-label"
                for="user-client-approver-role-display-enabled-tickbox"
              >
                Display as <b>client-approver</b>
              </div>
            </div>
            <div v-if="clientUserRoleDisplaySetupEnabled" class="d-flex align-center">
              <div>
                <v-checkbox-btn
                  id="user-client-user-role-display-enabled-tickbox"
                  v-model="clientUserRoleDisplayEnabled"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </div>
              <div
                class="client-individual-checkbox-label"
                for="user-client-user-role-display-enabled-tickbox"
              >
                Display as <b>client-user</b>
              </div>
            </div>
          </div>
          <v-row no-gutters class="user-individual-modal-row">
            <v-col v-if="clientsDisplayed" cols="12"> Clients </v-col>
            <v-col
              v-if="clientsDisplayed && hubClients.length > 0"
              cols="12"
              class="client-section"
            >
              <div class="client-section-label">Hub-assigned</div>
              <div id="client-user-individual-modal--hub-clients" class="hub-chip-list">
                <v-chip
                  v-for="hubClient in hubClients"
                  :id="`client-user-individual-modal--hub-client-chip-${hubClient.sid}`"
                  :key="hubClient.sid"
                  color="primary"
                  variant="tonal"
                  size="default"
                  class="hub-client-chip"
                  aria-readonly="true"
                  :title="hubChipTooltip"
                  tabindex="-1"
                >
                  <span class="hub-client-chip__dot"></span>
                  <span class="hub-client-chip__label">Hub</span>
                  <span class="hub-client-chip__name">{{ hubClient.name }}</span>
                  <span class="visually-hidden">Hub-assigned, not editable</span>
                  <v-tooltip activator="parent" location="top">
                    {{ hubChipTooltip }}
                  </v-tooltip>
                </v-chip>
              </div>
            </v-col>
            <v-col v-if="clientsDisplayed" cols="12" class="client-section">
              <div class="client-section-label">Manually assigned</div>
              <v-autocomplete
                id="client-user-individual-modal--clients"
                ref="selectClient"
                v-model="manualClients"
                v-model:search="searchClients"
                :items="clientItems"
                placeholder="Select one or more Clients"
                item-title="name"
                item-value="sid"
                density="compact"
                clearable
                multiple
                chips
                hide-details
                clear-icon="mdi-close"
                :hide-selected="hideSelected"
                closable-chips
                variant="solo"
                required
                return-object
                :error-messages="clientSidsErrors"
                @blur="v$.clientSids.$touch()"
                @update:model-value="handleClientChange"
                @focus="handleClientFocus"
                @close="handleClientClose"
              >
                <template #item="data">
                  <v-list-item
                    v-bind="data.props"
                    :id="`client-user-individual-modal--clients-item-${data.item.sid}`"
                  />
                </template>
              </v-autocomplete>
              <div v-if="clientSidsErrors.length > 0" class="pre-error">Clients are required</div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      v-model="genericDialogDisplayed"
      body-id="dlg-body-client-user-individual-modal-generic-msg"
      heading="Client User Individual Modal"
      :primary-action="{ label: 'OK', eventName: 'primary-click' }"
      @primary-click="genericDialogDisplayed = false"
    >
      <template #cs-form-dialog-content>
        <div v-safe-html="genericDialogMessage"></div>
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import * as types from '@/store/mutationTypes';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { cloneDeep } from 'lodash';
import aclRole from '../lib/const/aclRole';
import errorCaseSid from '../lib/const/errorCaseSid';
import { populateErrorMessage } from '../lib/errorMessages';

const HUB_CHIP_TOOLTIP = 'Assigned by Hub.';

export default {
  name: 'ClientUserIndividualModal',

  validations() {
    return {
      email: {
        required
      },
      clientAclRole: {
        required: (value) => {
          if (!this.selectedClients || this.selectedClients.length === 0) {
            return true;
          }
          return !!value;
        }
      },
      clientSids: {
        required
      }
    };
  },

  props: {
    propClientUserIndividualModalRefreshed: Boolean,
    propUserId: {
      type: String,
      required: false,
      default: ''
    }
  },
  emits: ['reloadUsers'],

  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      genericDialogDisplayed: false,
      clientUserSaveActionInProgress: false,
      genericDialogMessage: '',
      clientUserIndividualModalDisplayed: false,
      selectedClients: [],
      searchClients: '',
      pagination: {
        rowsPerPage: -1,
        descending: false,
        sortBy: '',
        page: 1,
        order: ''
      },
      clientAclRole: null,
      email: '',
      emailConflicted: false,
      emailValid: true,
      givenName: '',
      surname: '',
      clientAdminAssistantRoleDisplayEnabled: false,
      clientApproverRoleDisplayEnabled: false,
      clientUserRoleDisplayEnabled: false,
      hideSelected: false
    };
  },
  computed: {
    isHubEnabled() {
      return this.$store.getters['authorisation/hubEnabled'];
    },
    isHubManagedUser() {
      return this.isHubEnabled && this.hubClients.length > 0;
    },
    clientUserIndividualModalRefreshed() {
      return this.propClientUserIndividualModalRefreshed;
    },
    clientCache() {
      return this.$store.state.common.clientClientList.itemsCache;
    },
    hubChipTooltip() {
      return HUB_CHIP_TOOLTIP;
    },
    clientPartition() {
      return this.$store.getters['common/partitionUserClients'](this.selectedClients);
    },
    hubClients() {
      return this.clientPartition.hubClients;
    },
    manualClients: {
      get() {
        return this.clientPartition.manualClients;
      },
      set(newManualClients) {
        const manuals = Array.isArray(newManualClients) ? newManualClients : [];
        this.selectedClients = [...this.hubClients, ...manuals];
      }
    },
    clientSids() {
      return this.selectedClients.map((c) => c.sid);
    },
    clientItems() {
      const items = cloneDeep(this.$store.state.common.clientClientList.items);
      this.manualClients.forEach((client) => {
        if (!items.some((e) => e.sid === client.sid)) {
          items.push(client);
        }
      });
      const hubSids = new Set(this.hubClients.map((c) => c.sid));
      return items.filter((item) => !hubSids.has(item.sid));
    },
    emailErrors() {
      const errors = [];
      if (!this.v$.email.$dirty) return errors;
      if (this.v$.email.required.$invalid) {
        errors.push('Email is required');
      }

      if (!this.emailValid) {
        errors.push('Invalid Email');
      }

      if (this.emailConflicted) {
        errors.push('Email conflicted');
      }

      return errors;
    },
    clientAclRoleErrors() {
      const errors = [];
      if (!this.v$.clientAclRole.$dirty) return errors;
      if (this.v$.clientAclRole.required.$invalid) {
        errors.push('Client ACL Role is required');
      }

      return errors;
    },
    clientSidsErrors() {
      const errors = [];
      if (!this.v$.clientSids.$dirty) return errors;
      if (this.v$.clientSids.required.$invalid) {
        errors.push('Clients are required');
      }

      return errors;
    },
    clientAclRoles() {
      const clientAclRoles = [
        { value: aclRole.CLIENT_ADMIN, text: aclRole.CLIENT_ADMIN },
        {
          value: aclRole.CLIENT_ADMIN_ASSISTANT,
          text: aclRole.CLIENT_ADMIN_ASSISTANT
        },
        { value: aclRole.CLIENT_APPROVER, text: aclRole.CLIENT_APPROVER },
        { value: aclRole.CLIENT_LITE_USER, text: aclRole.CLIENT_LITE_USER },
        { value: aclRole.CLIENT_USER, text: aclRole.CLIENT_USER },
        { value: aclRole.CLIENT_WATCHER, text: aclRole.CLIENT_WATCHER }
      ];

      if (this.$store.state.common.clientUserGroupRoleDisplayed) {
        clientAclRoles.push(
          {
            value: aclRole.CLIENT_GROUP_ADMIN,
            text: aclRole.CLIENT_GROUP_ADMIN
          },
          {
            value: aclRole.CLIENT_GROUP_APPROVER,
            text: aclRole.CLIENT_GROUP_APPROVER
          },
          { value: aclRole.CLIENT_GROUP_USER, text: aclRole.CLIENT_GROUP_USER },
          {
            value: aclRole.CLIENT_GROUP_WATCHER,
            text: aclRole.CLIENT_GROUP_WATCHER
          }
        );
      }
      return clientAclRoles;
    },
    clientsDisplayed() {
      return this.$store.state.common.clientUserGroupRoleDisplayed;
    },
    clientAdminAssistantRoleDisplaySetupEnabled() {
      return this.checkIfInferiorRoleDisplaySetupEnabled(
        this.clientAclRole,
        aclRole.CLIENT_ADMIN_ASSISTANT
      );
    },
    clientApproverRoleDisplaySetupEnabled() {
      return this.checkIfInferiorRoleDisplaySetupEnabled(
        this.clientAclRole,
        aclRole.CLIENT_APPROVER
      );
    },
    clientUserRoleDisplaySetupEnabled() {
      return this.checkIfInferiorRoleDisplaySetupEnabled(this.clientAclRole, aclRole.CLIENT_USER);
    },
    inferiorRoleSetupDisplayed() {
      return (
        this.clientAdminAssistantRoleDisplaySetupEnabled ||
        this.clientApproverRoleDisplaySetupEnabled ||
        this.clientUserRoleDisplaySetupEnabled
      );
    }
  },
  watch: {
    clientUserIndividualModalRefreshed() {
      this.newUserModal(this.propUserId);
      this.clientUserIndividualModalDisplayed = true;
    },
    async searchClients(val) {
      await this.filterClients(val);
    }
  },
  async mounted() {
    this.clientUserIndividualModalDisplayed = true;
    await this.newUserModal(this.propUserId);
    await this.filterClients('');
  },
  methods: {
    alertSaveResult(cbResponse) {
      this.clientUserSaveActionInProgress = false;
      this.close();
      this.clear();
      if (cbResponse && cbResponse.data) {
        // success
        this.genericDialogMessage = 'User has been saved.';

        this.$emit('reloadUsers');
      } else if (cbResponse.errorCaseSid === errorCaseSid.CLIENT_ACCESSIBILITY_INVALID) {
        this.genericDialogMessage =
          'Error: You are not able to edit users who have been created in a different SafeTripBuilder site. Please contact our support team if you believe you should be able to edit this user.';
      } else {
        this.genericDialogMessage = 'Failed to save User information.';

        this.genericDialogMessage = populateErrorMessage({
          message: this.genericDialogMessage,
          store: this.$store
        });
      }
      this.genericDialogDisplayed = true;
    },
    clear() {
      this.v$.$reset();
      this.clientAclRole = null;
      this.email = '';
      this.emailValid = true;
      this.givenName = '';
      this.surname = '';
      this.selectedClients = [];
      this.clientAdminAssistantRoleDisplayEnabled = false;
      this.clientApproverRoleDisplayEnabled = false;
      this.clientUserRoleDisplayEnabled = false;
    },
    close() {
      this.clientUserIndividualModalDisplayed = false;
      this.clear();
    },
    initSelectClient() {
      if (this.$refs.selectClient === undefined) {
        return;
      }
      // Initialize the v-autocomplete hide-selected attribute
      this.$refs.selectClient.click();
      this.$nextTick(() => {
        this.$refs.selectClient.blur();
      });
    },
    handleClientChange() {
      this.$refs.selectClient.blur();
      this.$nextTick(() => {
        this.$refs.selectClient.click();
      });
    },
    handleClientFocus() {
      this.hideSelected = true;
    },
    handleClientClose() {
      this.hideSelected = false;
    },
    async newUserModal(id) {
      if (`${id}` !== '') {
        const argHash = {
          id,
          store: this.$store
        };
        const cbResponse = await this.$app.stbApiAdapter.getClientUsers(argHash);
        if (cbResponse && !cbResponse.error) {
          const user = cbResponse.data[0];
          this.email = user.email;
          const userMetadata = user.metadata;

          if (userMetadata) {
            this.givenName = userMetadata.givenName;
            this.surname = userMetadata.surname;
            this.clientAdminAssistantRoleDisplayEnabled =
              userMetadata.clientAdminAssistantRoleDisplayEnabled;
            this.clientApproverRoleDisplayEnabled = userMetadata.clientApproverRoleDisplayEnabled;
            this.clientUserRoleDisplayEnabled = userMetadata.clientUserRoleDisplayEnabled;
          } else {
            this.givenName = '';
            this.surname = '';
            this.clientAdminAssistantRoleDisplayEnabled = false;
            this.clientApproverRoleDisplayEnabled = false;
            this.clientUserRoleDisplayEnabled = false;
          }

          if (user.clientList && user.clientList.length > 0) {
            this.selectedClients = user.clientList.map((item) => {
              return {
                sid: item.sid,
                name: `${item.sid} - ${item.metadata.name}`,
                isHubAssigned: item.isHubAssigned === true,
                client_acl_role: item.client_acl_role
              };
            });
            const firstClient = this.selectedClients[0];
            if (firstClient) {
              this.clientAclRole = firstClient.client_acl_role;
            }
          }
        }
        this.initSelectClient();
      } else {
        this.clear();

        if (!this.$store.state.common.clientUserGroupRoleDisplayed) {
          this.selectedClients.push({ sid: this.$store.state.common.userClientSid });
        }
      }
    },
    async save() {
      this.emailValid = true;
      const regExp =
        /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

      if (!regExp.test(this.email)) {
        this.emailValid = false;
        return;
      }

      this.clientUserSaveActionInProgress = true;
      let clients = [];
      if (this.selectedClients && this.selectedClients.length > 0) {
        clients = this.selectedClients.map((c) => {
          return {
            sid: c.sid,
            'acl-role': this.clientAclRole
          };
        });
      }

      const apiParam = JSON.parse(JSON.stringify(this.$store.state.common.apiParam));
      const user = {
        email: String(this.email).trim(),
        metadata: {
          givenName: String(this.givenName).trim(),
          surname: String(this.surname).trim(),
          clientAdminAssistantRoleDisplayEnabled: this.clientAdminAssistantRoleDisplayEnabled,
          clientApproverRoleDisplayEnabled: this.clientApproverRoleDisplayEnabled,
          clientUserRoleDisplayEnabled: this.clientUserRoleDisplayEnabled
        },
        clients
      };

      apiParam.user = user;
      if (`${this.propUserId}` !== '') {
        const argHash = {
          id: this.propUserId,
          payload: apiParam,
          store: this.$store
        };
        const cbResponse = await this.$app.stbApiAdapter.putClientUsers(argHash);
        this.clientUserSaveActionInProgress = false;
        if (`${cbResponse.statusCode}` === '409') {
          this.emailConflicted = true;
        } else {
          this.emailConflicted = false;
          this.alertSaveResult(cbResponse);
        }
      } else {
        const argHash = {
          payload: apiParam,
          store: this.$store
        };
        const cbResponse = await this.$app.stbApiAdapter.postClientUsers(argHash);
        this.clientUserSaveActionInProgress = false;
        if (`${cbResponse.statusCode}` === '409') {
          this.emailConflicted = true;
        } else {
          this.emailConflicted = false;
          this.alertSaveResult(cbResponse);
        }
      }
    },
    checkIfInferiorRoleDisplaySetupEnabled(superiorClientAclRole, inferiorClientAclRole) {
      let inferiorRoleDisplaySetupEnabled = false;

      if (superiorClientAclRole) {
        const inferiorClientAclRoleHash =
          this.$store.state.common.clientAclRoleInferiorityHash[superiorClientAclRole];

        inferiorRoleDisplaySetupEnabled =
          inferiorClientAclRoleHash && inferiorClientAclRoleHash[inferiorClientAclRole];
      }

      return inferiorRoleDisplaySetupEnabled;
    },
    async filterClients(val) {
      let cacheItem;
      const localVal = val || '';
      if (this.clientCache && this.clientCache.length > 0) {
        cacheItem = this.clientCache.find(function (item) {
          return item.key === localVal;
        });
      }

      if (cacheItem) {
        this.$store.commit(types.COMMON_SET_CLIENT_CLIENT_LIST_ITEMS, cacheItem.items);
      } else {
        this.$store.commit(types.COMMON_SET_CLIENT_CLIENT_LIST_LOADING, true);
        const filterParam = {
          s: localVal,
          page: this.pagination.page,
          order: this.pagination.sortBy,
          length: 5,
          direction: this.pagination.descending === false ? 'ASC' : 'DESC'
        };

        Object.keys(filterParam).forEach((key) => {
          if (filterParam[key] === '') {
            delete filterParam[key];
          }
        });
        const argHash = {
          stbApiAdapter: this.$app.stbApiAdapter,
          filterParam,
          store: this.$store
        };
        await this.$store.dispatch('common/getClientClientList', argHash);
      }
    }
  }
};
</script>
<style scoped>
.form-container {
  font-size: 14px;
}

.user-individual-modal-row {
  margin-bottom: 20px;
}

.user-individual-modal-row-with-error {
  margin-bottom: 6px;
}

.pre-error {
  color: #ff5252;
  font-size: 12px;
}

.client-acl-role-helper {
  color: #5a5a5a;
  font-size: 12px;
}

.client-section {
  margin-top: 8px;
}

.client-section-label {
  font-size: 12px;
  color: #5a5a5a;
  margin-bottom: 4px;
}

.hub-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.hub-client-chip__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
  flex-shrink: 0;
  background: currentColor;
}

.hub-client-chip__label {
  font-weight: 600;
  margin-right: 6px;
}

.hub-client-chip__name {
  font-weight: 400;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
